import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Mail, User, MessageSquare } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  password: string;
}

export function UserRegistrationForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form
      if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim()) {
        throw new Error('Please fill in all required fields');
      }

      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      // Check if email is already registered
      const { data: existingUser } = await supabase.auth.getUser();
      if (existingUser) {
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('registration_approved')
          .eq('email', formData.email.toLowerCase())
          .single();

        if (existingProfile) {
          if (existingProfile.registration_approved) {
            throw new Error('This email is already registered and approved. Please sign in instead.');
          } else {
            throw new Error('This email is already registered and pending approval.');
          }
        }
      }

      // Create user account
      const { error: signUpError } = await supabase.auth.signUp({
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
        options: {
          data: {
            name: formData.name.trim()
          },
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (signUpError) throw signUpError;

      // Also create a registration request for admin tracking
      const { error: requestError } = await supabase
        .from('user_registration_requests')
        .insert({
          name: formData.name.trim(),
          email: formData.email.toLowerCase().trim(),
          requested_role: 'user',
          request_message: null
        });

      if (requestError) throw requestError;

      setSubmitted(true);
      toast({
        title: "Account Created - Pending Approval",
        description: "Your account has been created but requires admin approval before you can access the platform.",
      });

      // Send registration notification emails
      try {
        await supabase.functions.invoke('send-registration-emails', {
          body: {
            name: formData.name.trim(),
            email: formData.email.toLowerCase().trim()
          }
        });
        console.log('Registration emails sent successfully');
      } catch (emailError) {
        console.error('Failed to send registration emails:', emailError);
        // Don't block the registration process if emails fail
      }

      // Reset form
      setFormData({
        name: '',
        email: '',
        password: ''
      });

    } catch (err) {
      console.error('Error submitting registration request:', err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to submit registration request",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <Card className="max-w-md mx-auto">
        <CardContent className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Account Created - Awaiting Approval!</h3>
          <p className="text-muted-foreground mb-4">
            Your account has been created but requires admin approval. 
            You can try signing in after an administrator approves your request.
          </p>
          <Button 
            variant="outline" 
            onClick={() => setSubmitted(false)}
          >
            Submit Another Request
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Request Access
        </CardTitle>
        <CardDescription>
          Submit a request to join our platform. An administrator will review your request.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <Input
              id="password"
              type="password"
              placeholder="Create a password (min. 6 characters)"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              required
              disabled={loading}
              minLength={6}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <MessageSquare className="h-4 w-4 mr-2" />
                Submit Request
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            By submitting this request, you agree to our terms of service and privacy policy.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}